import React from "react";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import { Paper } from "@mui/material";

import numberColumn from "components/tables/columns/NumberColumn";
import textColumn from "components/tables/columns/TextColumn";
import { getUsageCategoryList, TokenUsage, UsageCategoryKey, UsageMetric } from "routes/apiUsage/types/Usage";
import { UsageApiToken } from "../tabs/UserUsage";
import StripedDataGrid from "components/uiElements/dataGrid/StripedDataGrid";

const UsageCategoryList = getUsageCategoryList();

interface TokenUsageDataGridProps {
  onClick?: (token: UsageApiToken) => void;
  tokenUsage: Array<TokenUsage>;
  usageDisplayMetric: UsageMetric;
}

export default function TokenUsageDataGrid(props: TokenUsageDataGridProps): React.JSX.Element {
  const { i18n } = useTranslation();

  function TokenValueGetter(value: any, row: TokenUsage, params: GridColDef): number {
    return row[props.usageDisplayMetric][params.field as UsageCategoryKey] || 0;
  }

  const columns = UsageCategoryList.map((key: UsageCategoryKey): GridColDef => {
    return {
      field: key,
      flex: 1,
      headerName: i18n.t(`usage.categories.${key}`),
      valueGetter: TokenValueGetter,
      ...numberColumn()
    };
  });

  columns.unshift({
    field: "description",
    flex: 2,
    headerName: i18n.t("token-usage-data-grid.token-description"),
    ...textColumn()
  });

  // Old API Tokens were not logged with their `id` and so they return as null.  MUI needs all rows
  // in the DataGrid to have a valid `id` so we need to generate one here to make MUI happy.
  const rows = props.tokenUsage.map((tokenUsage, index) => {
    if (!tokenUsage.id) {
      return {
        ...tokenUsage,
        id: `generated_id_${index}`
      }
    }
    return tokenUsage;
  });

  return (
    <Paper>
      <StripedDataGrid
        columns={columns}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: 10
            }
          }
        }}
        onRowClick={(params, event, details) => {
          if (props.onClick) {
            props.onClick(params.row);
          }
        }}
        pageSizeOptions={[10, 25, 50]}
        rows={rows}
        rowSelection={false}
        sortingOrder={["desc", "asc", null]}
        sx={{
          '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
            outline: 'none',
          },
          '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
            outline: 'none',
          },
          cursor: props.onClick ? "pointer" : ""
        }}
      />
    </Paper>
  )
}